// src/services/userService.js
import ApiService from './api'
import AppURLs from '../config/urls'
import { hasPermission } from '../config/roles'

class UserService {
  // Get all users - using correct endpoint
  async getAllUsers(params = {}) {
    try {
      console.log('👥 Fetching all users with params:', params)
      // Try the users endpoint that should exist
      const response = await ApiService.get('/users', params)
      console.log('✅ Users fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching users:', error)
      throw error
    }
  }

  // Get user by ID
  async getUserById(id) {
    try {
      console.log('👥 Fetching user by ID:', id)
      const response = await ApiService.get(`/users/${id}`)
      console.log('✅ User fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching user:', error)
      throw error
    }
  }

  // Get user profile
  async getUserProfile() {
    try {
      console.log('👤 Fetching user profile')
      const response = await ApiService.get(AppURLs.AUTH.PROFILE)
      console.log('✅ User profile fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching user profile:', error)
      throw error
    }
  }

  // Create new user
  async createUser(userData) {
    try {
      // Client-side password validation
      if (userData.password) {
        const { validatePassword } = await import('../utils/passwordValidation')
        const passwordValidation = validatePassword(userData.password)
        if (!passwordValidation.isValid) {
          throw new Error(`Password validation failed: ${passwordValidation.errors[0]}`)
        }
      }

      console.log('👥 Creating new user with data:', {
        ...userData,
        password: '***' // Don't log password
      })
      const response = await ApiService.post(AppURLs.AUTH.REGISTER, userData)
      console.log('✅ User created successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error creating user:', error)
      throw error
    }
  }

  // Update user using PATCH
  async updateUser(id, userData) {
    try {
      console.log('👥 Updating user:', { id, userData })
      const response = await ApiService.patch(`/users/${id}`, userData)
      console.log('✅ User updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating user:', error)
      throw error
    }
  }

  // Delete user
  async deleteUser(id) {
    try {
      console.log('👥 Deleting user:', id)
      const response = await ApiService.delete(`/users/${id}`)
      console.log('✅ User deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error deleting user:', error)
      throw error
    }
  }

  // Reset user password using PATCH
  async resetUserPassword(id, newPassword) {
    try {
      // Client-side password validation
      if (newPassword) {
        const { validatePassword } = await import('../utils/passwordValidation')
        const passwordValidation = validatePassword(newPassword)
        if (!passwordValidation.isValid) {
          throw new Error(`Password validation failed: ${passwordValidation.errors[0]}`)
        }
      }

      console.log('🔐 Resetting password for user:', id)
      const response = await ApiService.patch(`/users/${id}/reset-password`, { newPassword })
      console.log('✅ Password reset successfully')
      return response
    } catch (error) {
      console.error('❌ Error resetting password:', error)
      throw error
    }
  }

  // Toggle user status using PATCH
  async toggleUserStatus(id, isActive) {
    try {
      console.log('👥 Toggling user status:', { id, isActive })
      const response = await ApiService.patch(`/users/${id}/status`, { isActive })
      console.log('✅ User status toggled successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error toggling user status:', error)
      throw error
    }
  }

  // Search users
  async searchUsers(searchTerm, filters = {}) {
    try {
      console.log('👥 Searching users:', { searchTerm, filters })
      const params = {
        search: searchTerm,
        ...filters
      }
      const response = await ApiService.get('/users', params)
      console.log('✅ Users search completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error searching users:', error)
      throw error
    }
  }

  // Get users with pagination
  async getUsersPaginated(page = 1, limit = 10, filters = {}) {
    try {
      console.log('👥 Fetching paginated users:', { page, limit, filters })
      const params = {
        page,
        limit,
        ...filters
      }
      const response = await ApiService.get('/users', params)
      console.log('✅ Paginated users fetched:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching paginated users:', error)
      throw error
    }
  }

  // Bulk operations
  async bulkDeleteUsers(userIds) {
    try {
      console.log('👥 Bulk deleting users:', userIds)
      const response = await ApiService.post('/users/bulk-delete', {
        ids: userIds
      })
      console.log('✅ Bulk delete completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error bulk deleting users:', error)
      throw error
    }
  }

  async bulkUpdateUserStatus(userIds, isActive) {
    try {
      console.log('👥 Bulk updating user status:', { userIds, isActive })
      const response = await ApiService.post('/users/bulk-status', {
        ids: userIds,
        isActive
      })
      console.log('✅ Bulk status update completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error bulk updating user status:', error)
      throw error
    }
  }

  async bulkResetPasswords(userIds, newPassword) {
    try {
      // Client-side password validation
      if (newPassword) {
        const { validatePassword } = await import('../utils/passwordValidation')
        const passwordValidation = validatePassword(newPassword)
        if (!passwordValidation.isValid) {
          throw new Error(`Password validation failed: ${passwordValidation.errors[0]}`)
        }
      }

      console.log('👥 Bulk resetting passwords for users:', userIds)
      const response = await ApiService.post('/users/bulk-reset-password', {
        ids: userIds,
        newPassword
      })
      console.log('✅ Bulk password reset completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error bulk resetting passwords:', error)
      throw error
    }
  }

  // Export users
  async exportUsers(format = 'csv', filters = {}) {
    try {
      console.log('👥 Exporting users:', { format, filters })
      const params = {
        format,
        ...filters
      }
      const response = await ApiService.get('/users/export', params)
      console.log('✅ Users exported:', response)
      return response
    } catch (error) {
      console.error('❌ Error exporting users:', error)
      throw error
    }
  }

  // Get user statistics
  async getUserStats() {
    try {
      console.log('👥 Fetching user statistics')
      const response = await ApiService.get('/users/stats')
      console.log('✅ User stats fetched:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching user stats:', error)
      throw error
    }
  }

  // Get users by role
  async getUsersByRole(role, params = {}) {
    try {
      console.log('👥 Fetching users by role:', role)
      const searchParams = {
        role,
        ...params
      }
      const response = await ApiService.get('/users', searchParams)
      console.log('✅ Users by role fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching users by role:', error)
      throw error
    }
  }

  // Get users by branch
  async getUsersByBranch(branchId, params = {}) {
    try {
      console.log('👥 Fetching users by branch:', branchId)
      const searchParams = {
        branchId,
        ...params
      }
      const response = await ApiService.get('/users', searchParams)
      console.log('✅ Users by branch fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching users by branch:', error)
      throw error
    }
  }

  // Change user password (different from reset - might require current password)
  async changeUserPassword(id, currentPassword, newPassword) {
    try {
      // Client-side password validation for new password
      if (newPassword) {
        const { validatePassword } = await import('../utils/passwordValidation')
        const passwordValidation = validatePassword(newPassword)
        if (!passwordValidation.isValid) {
          throw new Error(`New password validation failed: ${passwordValidation.errors[0]}`)
        }
      }

      console.log('👥 Changing password for user:', id)
      const response = await ApiService.patch(`/users/${id}/change-password`, {
        currentPassword,
        newPassword
      })
      console.log('✅ Password changed successfully')
      return response
    } catch (error) {
      console.error('❌ Error changing password:', error)
      throw error
    }
  }

  // Reset user password with old password (Admin functionality)
  async resetUserPasswordWithOldPassword(userId, oldPassword, newPassword) {
    try {
      // Client-side password validation for new password
      if (newPassword) {
        const { validatePassword } = await import('../utils/passwordValidation')
        const passwordValidation = validatePassword(newPassword)
        if (!passwordValidation.isValid) {
          throw new Error(`New password validation failed: ${passwordValidation.errors[0]}`)
        }
      }

      console.log('🔐 Resetting password for user (with old password):', userId)
      const response = await ApiService.post('/auth/change-password', {
        user: userId,
        oldPassword,
        newPassword
      })
      console.log('✅ Password reset with old password successfully')
      return response
    } catch (error) {
      console.error('❌ Error resetting password with old password:', error)
      throw error
    }
  }

  // Update user profile (for current user)
  async updateUserProfile(userData) {
    try {
      console.log('👤 Updating user profile:', userData)
      const response = await ApiService.patch(AppURLs.AUTH.PROFILE, userData)
      console.log('✅ User profile updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating user profile:', error)
      throw error
    }
  }

  // Get user roles (static for now - could be dynamic from API later)
  getUserRoles() {
    return [
      { value: 'admin', label: 'Admin' },
      { value: 'super_admin', label: 'Super Admin' },
      { value: 'manager', label: 'Manager' },
      { value: 'cashier', label: 'Cashier' },
      { value: 'barista', label: 'Barista' },
      { value: 'warehouse', label: 'Warehouse' }
    ]
  }

  // Validate user data
  validateUserData(userData) {
    const errors = {}
    
    // Username validation
    if (!userData.username || userData.username.trim().length < 3) {
      errors.username = 'Username must be at least 3 characters'
    }
    
    // Role validation
    const validRoles = this.getUserRoles().map(role => role.value)
    if (!userData.role || !validRoles.includes(userData.role)) {
      errors.role = 'Please select a valid role'
    }
    
    // Branch validation (required for non-admin roles)
    if (userData.role !== 'admin' && !userData.branchId) {
      errors.branchId = 'Branch is required for this role'
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }

  // Get user display name
  getUserDisplayName(user) {
    if (!user) return 'Unknown User'
    return user.username || user.email || 'Unknown User'
  }

  // Get user role display name
  getUserRoleDisplayName(role) {
    const roleObj = this.getUserRoles().find(r => r.value === role)
    return roleObj ? roleObj.label : role
  }

  // Check if user has permission
  userHasPermission(user, permission) {
    if (!user || !user.role) return false
    
    // Use imported hasPermission function
    return hasPermission(user.role, permission)
  }
}

export default new UserService()